<template>
  <Dialog
    v-model:visible="isVisible"
    :header="dialogHeader"
    modal
    class="w-full md:w-[60rem]"
    :dismissableMask="true">
    <div v-if="loading && !record" class="p-8 text-center text-gray-400">
      <i class="pi pi-spin pi-spinner text-2xl mb-2"></i>
      <p class="text-sm">Loading import details…</p>
    </div>

    <div v-else-if="!record" class="p-8 text-center text-gray-400">
      <i class="pi pi-times-circle text-2xl mb-2"></i>
      <p class="text-sm">Could not load this import.</p>
    </div>

    <div v-else class="flex flex-col gap-4">
      <!-- Summary -->
      <div class="grid grid-cols-3 gap-4 p-3 rounded-lg border bg-gray-50">
        <div class="text-center">
          <p class="text-xl font-bold text-gray-800">{{ record.total_rows.toLocaleString() }}</p>
          <p class="text-xs text-gray-500">Total Rows</p>
        </div>
        <div class="text-center">
          <p class="text-xl font-bold text-green-600">{{ record.successful_rows.toLocaleString() }}</p>
          <p class="text-xs text-gray-500">Imported</p>
        </div>
        <div class="text-center">
          <p class="text-xl font-bold text-red-600">{{ record.failed_rows.toLocaleString() }}</p>
          <p class="text-xs text-gray-500">Failed</p>
        </div>
      </div>

      <!-- Status / overall error message -->
      <div v-if="record.status === 'failed'" class="p-3 rounded bg-red-50 border border-red-200 text-sm text-red-700">
        <strong>Import failed.</strong> {{ record.error_message || 'No further details.' }}
      </div>

      <!-- Per-row errors -->
      <div v-if="errorRows.length > 0">
        <div class="flex items-center justify-between mb-2">
          <h4 class="font-semibold text-gray-700 text-sm">Failed Rows ({{ errorRows.length.toLocaleString() }})</h4>
          <Button
            label="Download CSV"
            icon="pi pi-download"
            text
            size="small"
            @click="downloadErrorsCsv"
          />
        </div>
        <div class="border rounded-lg overflow-hidden max-h-[420px] overflow-y-auto">
          <table class="w-full text-xs">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="px-3 py-2 text-left font-semibold text-gray-700 w-20">Row</th>
                <th class="px-3 py-2 text-left font-semibold text-gray-700 w-40">Field</th>
                <th class="px-3 py-2 text-left font-semibold text-gray-700">Errors</th>
              </tr>
            </thead>
            <tbody class="divide-y">
              <template v-for="row in errorRows" :key="`row-${row.row}`">
                <tr v-for="(messages, field) in row.errors" :key="`${row.row}-${field}`" class="hover:bg-gray-50">
                  <td class="px-3 py-1.5 text-gray-500 align-top">{{ row.row }}</td>
                  <td class="px-3 py-1.5 text-gray-700 font-medium align-top">{{ field }}</td>
                  <td class="px-3 py-1.5 text-red-600 align-top">
                    <div v-for="(msg, idx) in normalizeMessages(messages)" :key="idx">{{ msg }}</div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <p class="text-[11px] text-gray-400 mt-2">
          Successful rows are already imported. Fix the rows above and re-import only those.
        </p>
      </div>

      <div v-else class="p-6 text-center text-gray-400">
        <i class="pi pi-check-circle text-2xl mb-2 text-green-500"></i>
        <p class="text-sm">No row-level errors recorded.</p>
      </div>
    </div>

    <template #footer>
      <Button size="small" label="Close" icon="pi pi-times" text @click="isVisible = false" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useStudentImportPolling } from '@/composables/queries/useStudentImport';
import type { ImportError } from '@/types/composition-functions';

const props = defineProps<{
  visible: boolean;
  studentImportId: string | null;
}>();

const emit = defineEmits<{ 'update:visible': [value: boolean] }>();

const isVisible = computed({
  get: () => props.visible,
  set: (v) => emit('update:visible', v),
});

const { record, loading, start, stop } = useStudentImportPolling({ intervalMs: 5000 });

watch(() => [props.visible, props.studentImportId] as const, ([visible, id]) => {
  if (visible && id) {
    start(id);
  } else if (!visible) {
    stop();
  }
}, { immediate: true });

const errorRows = computed<ImportError[]>(() => {
  const errs = record.value?.errors;
  if (!Array.isArray(errs)) return [];
  return errs.filter((e): e is ImportError => !!e && e.row !== null && e.row !== undefined && !!e.errors);
});

const dialogHeader = computed(() => {
  const r = record.value;
  if (!r) return 'Import Details';
  if (r.failed_rows > 0) return `Import errors — ${r.failed_rows.toLocaleString()} failed row${r.failed_rows === 1 ? '' : 's'}`;
  return 'Import Details';
});

function normalizeMessages(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((m) => String(m));
  if (value === null || value === undefined) return [];
  return [String(value)];
}

function downloadErrorsCsv() {
  if (errorRows.value.length === 0) return;
  const rows: string[] = ['row,field,error'];
  for (const item of errorRows.value) {
    const fields = item.errors as Record<string, unknown>;
    for (const [field, messages] of Object.entries(fields)) {
      for (const msg of normalizeMessages(messages)) {
        const safe = (s: string) => `"${s.replace(/"/g, '""')}"`;
        rows.push([item.row, safe(field), safe(msg)].join(','));
      }
    }
  }
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `student-import-errors-${record.value?.id || 'unknown'}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

onBeforeUnmount(() => stop());
</script>
